
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    CalendarDays, RefreshCw, AlertCircle, MapPin, Phone, 
    X, Save, Image as ImageIcon, CheckCircle,
    Plus, Clock, Calendar, Eye, Pencil, ImageOff, Play,
    Maximize2, ExternalLink, Flame, Timer, CheckCheck, Lock,
    Search, ArrowUpDown, SlidersHorizontal
} from 'lucide-react';
import { eventoService } from '../../services/eventoService';
import { useNavigate } from 'react-router-dom';

// ============================================================
// UTILIDADES: TOKEN Y PERMISOS
// ============================================================

const getUserType = () => {
    try {
        const token = localStorage.getItem("auth_token");
        if (!token) return null;
        const parts = token.split('.');
        if (parts.length >= 2) {
            const payload = JSON.parse(atob(parts[0]));
            return payload.tipo_user || payload.tipo_usuario || null;
        }
        return null;
    } catch (e) {
        return null;
    }
};

const isAdmin = () => {
    const tipo = getUserType();
    return tipo === 'admin';
};

// ============================================================
// UTILIDADES: ESTADO DEL EVENTO POR FECHA
// ============================================================

const getEventStatus = (fechaInicio, fechaFin) => {
    const ahora = new Date();
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);

    if (isNaN(inicio.getTime()) || isNaN(fin.getTime())) {
        return { status: 'unknown', label: 'Desconocido', color: 'gray' };
    }

    if (ahora >= inicio && ahora <= fin) {
        return { 
            status: 'active', 
            label: 'En curso', 
            bg: 'bg-red-500',
            text: 'text-white',
            lightBg: 'bg-red-50',
            lightText: 'text-red-700',
            lightBorder: 'border-red-200',
            icon: Flame,
            pulse: true
        };
    }

    if (ahora > fin) {
        return { 
            status: 'past', 
            label: 'Finalizado', 
            bg: 'bg-gray-500',
            text: 'text-white',
            lightBg: 'bg-gray-50',
            lightText: 'text-gray-600',
            lightBorder: 'border-gray-200',
            icon: CheckCheck,
            pulse: false
        };
    }

    const diffMs = inicio - ahora;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));

    if (diffHours <= 24 && diffHours > 0) {
        return { 
            status: 'soon', 
            label: `En ${diffHours}h`, 
            bg: 'bg-orange-500',
            text: 'text-white',
            lightBg: 'bg-orange-50',
            lightText: 'text-orange-700',
            lightBorder: 'border-orange-200',
            icon: Timer,
            pulse: true
        };
    }

    if (diffDays <= 7 && diffDays > 0) {
        return { 
            status: 'upcoming', 
            label: `En ${diffDays} días`, 
            bg: 'bg-amber-500',
            text: 'text-white',
            lightBg: 'bg-amber-50',
            lightText: 'text-amber-700',
            lightBorder: 'border-amber-200',
            icon: Timer,
            pulse: false
        };
    }

    return { 
        status: 'future', 
        label: `${diffDays} días`, 
        bg: 'bg-emerald-500',
        text: 'text-white',
        lightBg: 'bg-emerald-50',
        lightText: 'text-emerald-700',
        lightBorder: 'border-emerald-200',
        icon: Calendar,
        pulse: false
    };
};

// ============================================================
// UTILIDADES: DETECCION DE MULTIMEDIA
// ============================================================

const detectMediaType = (url) => {
    if (!url || typeof url !== 'string') return 'none';
    const lower = url.toLowerCase().trim();

    if (/youtube\.com\/watch\?v=/.test(lower) || /youtu\.be\//.test(lower) || /youtube\.com\/embed\//.test(lower)) {
        return 'youtube';
    }
    if (/vimeo\.com\/(\d+)/.test(lower) || /player\.vimeo\.com\/video\//.test(lower)) {
        return 'vimeo';
    }
    if (/\.(mp4|webm|ogg|mov|mkv|avi)(\?.*)?$/.test(lower)) {
        return 'video';
    }
    if (/\.(jpg|jpeg|png|gif|webp|bmp|svg|ico)(\?.*)?$/.test(lower)) {
        return 'image';
    }
    if (lower.startsWith('http')) {
        return 'image';
    }
    return 'none';
};

const extractYouTubeId = (url) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
};

const extractVimeoId = (url) => {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : null;
};

const getYouTubeThumbnail = (url) => {
    const id = extractYouTubeId(url);
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};

// ============================================================
// UTILIDADES: WHATSAPP
// ============================================================

const extractPhoneNumber = (contactText) => {
    if (!contactText) return null;
    const digits = contactText.replace(/\D/g, '');
    if (digits.length === 9) return `51${digits}`;
    if (digits.length > 9) return digits;
    return null;
};

// ============================================================
// COMPONENTE: BADGE DE TIPO DE MULTIMEDIA
// ============================================================

const MediaTypeBadge = ({ url }) => {
    const type = detectMediaType(url);
    const labels = {
        image: { text: 'FOTO', bg: 'bg-black/50', textColor: 'text-white' },
        video: { text: 'VIDEO', bg: 'bg-purple-600/70', textColor: 'text-white' },
        youtube: { text: 'YOUTUBE', bg: 'bg-red-600/70', textColor: 'text-white' },
        vimeo: { text: 'VIMEO', bg: 'bg-blue-500/70', textColor: 'text-white' },
        none: { text: 'SIN MEDIA', bg: 'bg-gray-400/50', textColor: 'text-white' }
    };
    const style = labels[type] || labels.none;
    return (
        <span className={`px-1.5 py-0.5 ${style.bg} backdrop-blur-sm ${style.textColor} text-[9px] font-bold rounded-md`}>
            {style.text}
        </span>
    );
};

// ============================================================
// COMPONENTE: BADGE DE ESTADO DEL EVENTO
// ============================================================

const StatusBadge = ({ fechaInicio, fechaFin }) => {
    const status = getEventStatus(fechaInicio, fechaFin);
    const Icon = status.icon;

    return (
        <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${status.lightBg} ${status.lightBorder} border`}>
            <div className="relative flex items-center justify-center">
                <Icon className={`w-3 h-3 ${status.lightText}`} />
                {status.pulse && (
                    <span className={`absolute inline-flex h-full w-full rounded-full ${status.bg} opacity-40 animate-ping`}></span>
                )}
            </div>
            <span className={`text-[10px] font-bold ${status.lightText} uppercase tracking-wider`}>
                {status.label}
            </span>
        </div>
    );
};

// ============================================================
// COMPONENTE: MINIATURA DE MULTIMEDIA (TARJETA)
// ============================================================

const EventMediaThumbnail = ({ src, alt, onExpand }) => {
    const [imgError, setImgError] = useState(false);
    const mediaType = detectMediaType(src);

    if (!src || mediaType === 'none') {
        return (
            <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center text-gray-400 w-full aspect-[3/4]">
                <ImageOff className="w-10 h-10 mb-2 opacity-30" />
                <span className="text-[10px] font-medium opacity-50 tracking-wide uppercase">Sin multimedia</span>
            </div>
        );
    }

    if (mediaType === 'image') {
        if (imgError) {
            return (
                <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center text-gray-400 w-full aspect-[3/4]">
                    <ImageOff className="w-10 h-10 mb-2 opacity-30" />
                    <span className="text-[10px] font-medium opacity-50">Error al cargar</span>
                </div>
            );
        }
        return (
            <div 
                className="relative w-full aspect-[3/4] overflow-hidden cursor-pointer group bg-gray-900"
                onClick={() => onExpand && onExpand(src, 'image')}
            >
                <img 
                    src={src} 
                    alt={alt}
                    className="w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-110"
                    onError={() => setImgError(true)}
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-500 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transform scale-50 group-hover:scale-100 transition-all duration-300 shadow-lg">
                        <Maximize2 className="w-5 h-5 text-gray-800" />
                    </div>
                </div>
                <div className="absolute top-2 left-2">
                    <MediaTypeBadge url={src} />
                </div>
            </div>
        );
    }

    if (mediaType === 'video') {
        return (
            <div className="relative w-full aspect-[3/4] overflow-hidden bg-gray-900 group">
                <video 
                    src={src}
                    className="w-full h-full object-cover"
                    preload="metadata"
                    muted
                    playsInline
                />
                <div 
                    className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center cursor-pointer"
                    onClick={() => onExpand && onExpand(src, 'video')}
                >
                    <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-xl transform group-hover:scale-110 transition-transform duration-300">
                        <Play className="w-6 h-6 text-gray-900 ml-0.5" fill="currentColor" />
                    </div>
                </div>
                <div className="absolute top-2 left-2">
                    <MediaTypeBadge url={src} />
                </div>
            </div>
        );
    }

    if (mediaType === 'youtube') {
        const videoId = extractYouTubeId(src);
        const thumbnail = getYouTubeThumbnail(src);
        if (!videoId) {
            return (
                <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center text-gray-400 w-full aspect-[3/4]">
                    <ImageOff className="w-10 h-10 mb-2 opacity-30" />
                    <span className="text-[10px] font-medium opacity-50">URL inválida</span>
                </div>
            );
        }
        return (
            <div 
                className="relative w-full aspect-[3/4] overflow-hidden bg-gray-900 cursor-pointer group"
                onClick={() => onExpand && onExpand(src, 'youtube')}
            >
                {thumbnail && !imgError ? (
                    <img 
                        src={thumbnail} 
                        alt={alt}
                        className="w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-110"
                        onError={() => setImgError(true)}
                        loading="lazy"
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-red-900 to-red-700 flex items-center justify-center">
                        <Play className="w-12 h-12 text-white/50" />
                    </div>
                )}
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
                    <div className="w-14 h-10 bg-red-600 rounded-lg flex items-center justify-center shadow-xl transform group-hover:scale-110 transition-transform duration-300">
                        <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                    </div>
                </div>
                <div className="absolute top-2 left-2">
                    <MediaTypeBadge url={src} />
                </div>
            </div>
        );
    }

    if (mediaType === 'vimeo') {
        const videoId = extractVimeoId(src);
        if (!videoId) {
            return (
                <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center text-gray-400 w-full aspect-[3/4]">
                    <ImageOff className="w-10 h-10 mb-2 opacity-30" />
                    <span className="text-[10px] font-medium opacity-50">URL inválida</span>
                </div>
            );
        }
        return (
            <div 
                className="relative w-full aspect-[3/4] overflow-hidden bg-gray-900 cursor-pointer group"
                onClick={() => onExpand && onExpand(src, 'vimeo')}
            >
                <div className="w-full h-full bg-gradient-to-br from-blue-900 to-cyan-800 flex items-center justify-center">
                    <Play className="w-12 h-12 text-white/30" />
                </div>
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-xl transform group-hover:scale-110 transition-transform duration-300">
                        <Play className="w-6 h-6 text-blue-600 ml-0.5" fill="currentColor" />
                    </div>
                </div>
                <div className="absolute top-2 left-2">
                    <MediaTypeBadge url={src} />
                </div>
            </div>
        );
    }

    return null;
};

// ============================================================
// COMPONENTE: PREVIEW EN MODAL (crear/editar)
// ============================================================

const EventMediaPreview = ({ src }) => {
    const [imgError, setImgError] = useState(false);
    const mediaType = detectMediaType(src);

    if (!src || mediaType === 'none') {
        return (
            <div className="bg-gray-100 rounded-xl border border-gray-200 overflow-hidden aspect-[4/3] flex items-center justify-center">
                <div className="flex flex-col items-center text-gray-400">
                    <ImageOff className="w-10 h-10 mb-2 opacity-30" />
                    <span className="text-xs font-medium opacity-50">Sin multimedia</span>
                </div>
            </div>
        );
    }

    if (mediaType === 'image') {
        if (imgError) {
            return (
                <div className="bg-gray-100 rounded-xl border border-gray-200 overflow-hidden aspect-[4/3] flex items-center justify-center">
                    <span className="text-gray-400 text-sm">Error al cargar imagen</span>
                </div>
            );
        }
        return (
            <div className="bg-gray-900 rounded-xl border border-gray-200 overflow-hidden aspect-[4/3] flex items-center justify-center">
                <img 
                    src={src} 
                    alt="Preview"
                    className="max-w-full max-h-full object-contain"
                    onError={() => setImgError(true)}
                />
            </div>
        );
    }

    if (mediaType === 'video') {
        return (
            <div className="bg-black rounded-xl border border-gray-200 overflow-hidden aspect-[4/3] flex items-center justify-center">
                <video 
                    src={src}
                    controls
                    className="w-full h-full"
                    preload="metadata"
                />
            </div>
        );
    }

    if (mediaType === 'youtube') {
        const videoId = extractYouTubeId(src);
        if (!videoId) return (
            <div className="bg-gray-100 rounded-xl border border-gray-200 overflow-hidden aspect-[4/3] flex items-center justify-center">
                <span className="text-gray-400 text-sm">URL de YouTube inválida</span>
            </div>
        );
        return (
            <div className="bg-black rounded-xl border border-gray-200 overflow-hidden aspect-[4/3]">
                <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title="YouTube video player"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            </div>
        );
    }

    if (mediaType === 'vimeo') {
        const videoId = extractVimeoId(src);
        if (!videoId) return (
            <div className="bg-gray-100 rounded-xl border border-gray-200 overflow-hidden aspect-[4/3] flex items-center justify-center">
                <span className="text-gray-400 text-sm">URL de Vimeo inválida</span>
            </div>
        );
        return (
            <div className="bg-black rounded-xl border border-gray-200 overflow-hidden aspect-[4/3]">
                <iframe
                    src={`https://player.vimeo.com/video/${videoId}`}
                    title="Vimeo video player"
                    className="w-full h-full"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                />
            </div>
        );
    }

    return null;
};

// ============================================================
// UTILIDADES DE FECHA
// ============================================================

const formatFecha = (fechaStr) => {
    if (!fechaStr) return '-';
    const fecha = new Date(fechaStr);
    if (isNaN(fecha.getTime())) return fechaStr;
    return fecha.toLocaleDateString('es-PE', { 
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
    });
};

const formatFechaCorta = (fechaStr) => {
    if (!fechaStr) return '-';
    const fecha = new Date(fechaStr);
    if (isNaN(fecha.getTime())) return fechaStr;
    return fecha.toLocaleDateString('es-PE', { 
        day: 'numeric', month: 'short'
    });
};

const formatHora = (fechaStr) => {
    if (!fechaStr) return '-';
    const fecha = new Date(fechaStr);
    if (isNaN(fecha.getTime())) return fechaStr;
    return fecha.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
};

const formatFechaInput = (fechaStr) => {
    if (!fechaStr) return '';
    const fecha = new Date(fechaStr);
    if (isNaN(fecha.getTime())) return fechaStr.slice(0, 16);
    const offset = fecha.getTimezoneOffset() * 60000;
    const localISOTime = new Date(fecha.getTime() - offset).toISOString().slice(0, 16);
    return localISOTime;
};

// ============================================================
// COMPONENTE PRINCIPAL: EVENTOS
// ============================================================

const Eventos = () => {
    const [eventos, setEventos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [userIsAdmin, setUserIsAdmin] = useState(false);
    const navigate = useNavigate();

    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [showFilters, setShowFilters] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentEvento, setCurrentEvento] = useState(null);
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });

     const initialLoad = useRef(false);

    const [expandedMedia, setExpandedMedia] = useState(null);

    useEffect(() => {
        setUserIsAdmin(isAdmin());
    }, []);

    const cargarEventos = async () => {
        setLoading(true);
        setError('');
        const result = await eventoService.listarEventos();

        if (result.success) {
            setEventos(result.data);
        } else {
            setError(result.error);
            if (result.code === 401 || result.code === 403) {
                setTimeout(() => navigate('/login'), 2000);
            }
        }
        setLoading(false);
    };

useEffect(() => {
    if (initialLoad.current) return;
    initialLoad.current = true;
    cargarEventos();
}, []);

    const filteredAndSortedEventos = useMemo(() => {
        let result = [...eventos];

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            result = result.filter(e => {
                const desc = (e.descripcion || '').toLowerCase();
                const centro = (e.centro_servicio || '').toLowerCase();
                const contacto = (e.contacto || '').toLowerCase();
                return desc.includes(q) || centro.includes(q) || contacto.includes(q);
            });
        }

        result.sort((a, b) => {
            const dateA = new Date(a.fercha_inicio);
            const dateB = new Date(b.fercha_inicio);
            if (sortOrder === 'asc') {
                return dateA - dateB;
            } else {
                return dateB - dateA;
            }
        });

        return result;
    }, [eventos, searchQuery, sortOrder]);

    const resetForm = () => {
        setFormData({
            fercha_inicio: '',
            fecha_fin: '',
            centro_servicio: '',
            contacto: '',
            descripcion: '',
            mutilemedia: ''
        });
        setSaveMessage({ type: '', text: '' });
    };

    const handleOpenCreate = () => {
        if (!userIsAdmin) return;
        setIsEditing(false);
        setCurrentEvento(null);
        resetForm();
        setIsModalOpen(true);
    };

    const handleOpenEdit = (evento) => {
        if (!userIsAdmin) return;
        setIsEditing(true);
        setCurrentEvento(evento);
        setFormData({
            fercha_inicio: formatFechaInput(evento.fercha_inicio),
            fecha_fin: formatFechaInput(evento.fecha_fin),
            centro_servicio: evento.centro_servicio || '',
            contacto: evento.contacto || '',
            descripcion: evento.descripcion || '',
            mutilemedia: evento.mutilemedia || ''
        });
        setSaveMessage({ type: '', text: '' });
        setIsModalOpen(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e) => {
        if (e) e.preventDefault();
        if (!userIsAdmin) return;

        setSaving(true);
        setSaveMessage({ type: '', text: '' });

        try {
            let result;

            if (isEditing && currentEvento) {
                const camposAEnviar = {};
                const camposOriginales = {
                    fercha_inicio: formatFechaInput(currentEvento.fercha_inicio),
                    fecha_fin: formatFechaInput(currentEvento.fecha_fin),
                    centro_servicio: currentEvento.centro_servicio || '',
                    contacto: currentEvento.contacto || '',
                    descripcion: currentEvento.descripcion || '',
                    mutilemedia: currentEvento.mutilemedia || ''
                };

                Object.keys(formData).forEach(key => {
                    if (String(formData[key]) !== String(camposOriginales[key])) {
                        camposAEnviar[key] = formData[key];
                    }
                });

                if (Object.keys(camposAEnviar).length === 0) {
                    setSaveMessage({ type: 'info', text: 'No se realizaron cambios.' });
                    setSaving(false);
                    return;
                }

                result = await eventoService.editarEvento(currentEvento.id_evento, camposAEnviar);
            } else {
                result = await eventoService.registrarEvento(formData);
            }

            if (result.success) {
                setSaveMessage({ type: 'success', text: result.mensaje });
                setTimeout(() => {
                    setIsModalOpen(false);
                    cargarEventos();
                }, 1500);
            } else {
                setSaveMessage({ type: 'error', text: result.error });
                if (result.code === 401 || result.code === 403) {
                    setTimeout(() => navigate('/login'), 2000);
                }
            }
        } catch (error) {
            console.error('Error al guardar:', error);
            setSaveMessage({ type: 'error', text: 'Error inesperado al guardar.' });
        }

        setSaving(false);
    };

    const containerVariants = { 
        hidden: { opacity: 0 }, 
        visible: { opacity: 1, transition: { staggerChildren: 0.06 } } 
    };
    const itemVariants = { 
        hidden: { y: 30, opacity: 0 }, 
        visible: { y: 0, opacity: 1, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } } 
    };

    const eventosActivos = eventos.filter(e => getEventStatus(e.fercha_inicio, e.fecha_fin).status === 'active').length;
    const eventosProximos = eventos.filter(e => {
        const s = getEventStatus(e.fercha_inicio, e.fecha_fin).status;
        return s === 'soon' || s === 'upcoming';
    }).length;

    return (
        <div className="p-4 lg:p-6 xl:p-8 w-full relative min-h-screen bg-gray-50/50">

            {/* === ENCABEZADO UNIFICADO: TÍTULO + CONTROLES EN UNA SOLA FILA === */}
            <motion.div 
                initial={{ opacity: 0, y: -10 }} 
                animate={{ opacity: 1, y: 0 }} 
                className="mb-6"
            >
                {/* Fila 1: Título + badges + botones principales (siempre visible) */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 flex items-center gap-3 tracking-tight">
                            <CalendarDays className="w-7 h-7 lg:w-8 lg:h-8 text-blue-600" /> 
                            Gestión de Eventos
                        </h1>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-sm font-medium">
                                {eventos.length} {eventos.length === 1 ? 'evento' : 'eventos'}
                            </span>
                            {eventosActivos > 0 && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-red-50 border border-red-200 rounded-full text-[10px] font-bold text-red-600">
                                    <Flame className="w-3 h-3" />
                                    {eventosActivos} en curso
                                </span>
                            )}
                            {eventosProximos > 0 && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded-full text-[10px] font-bold text-amber-600">
                                    <Timer className="w-3 h-3" />
                                    {eventosProximos} próximos
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        {userIsAdmin && (
                            <motion.button 
                                whileHover={{ scale: 1.03 }} 
                                whileTap={{ scale: 0.97 }} 
                                onClick={handleOpenCreate}
                                className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl shadow-lg shadow-gray-900/20 hover:bg-gray-800 transition-all duration-200 shrink-0 font-medium text-sm"
                            >
                                <Plus className="w-4 h-4" />
                                <span className="hidden sm:inline">Nuevo Evento</span>
                                <span className="sm:hidden">Nuevo</span>
                            </motion.button>
                        )}

                        <motion.button 
                            whileHover={{ scale: 1.03 }} 
                            whileTap={{ scale: 0.97 }} 
                            onClick={cargarEventos} 
                            disabled={loading} 
                            className="flex items-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 disabled:opacity-50 shrink-0 font-medium text-sm"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            <span className="hidden sm:inline">{loading ? 'Actualizando...' : 'Actualizar'}</span>
                            <span className="sm:hidden">{loading ? '...' : 'Act.'}</span>
                        </motion.button>
                    </div>
                </div>

                {/* Fila 2: Búsqueda + ordenamiento (misma fila, responsive) */}
                {!loading && !error && eventos.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2"
                    >
                        {/* Buscador */}
                        <div className="relative flex-1 min-w-0">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar por descripción, ubicación o contacto..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all shadow-sm"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <X className="w-3.5 h-3.5 text-gray-400" />
                                </button>
                            )}
                        </div>

                        {/* Controles de ordenamiento */}
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                                className="flex items-center gap-1.5 px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm font-medium whitespace-nowrap"
                                title={sortOrder === 'asc' ? 'Más reciente primero' : 'Más lejano primero'}
                            >
                                <ArrowUpDown className="w-3.5 h-3.5" />
                                <span className="hidden md:inline">
                                    {sortOrder === 'asc' ? 'Reciente → Futuro' : 'Futuro → Reciente'}
                                </span>
                            </button>
                        </div>

                        {searchQuery && (
                            <span className="text-xs text-gray-400 font-medium whitespace-nowrap shrink-0 self-center">
                                {filteredAndSortedEventos.length} resultado{filteredAndSortedEventos.length !== 1 ? 's' : ''}
                            </span>
                        )}
                    </motion.div>
                )}
            </motion.div>

            {/* ESTADO DE ERROR */}
            {error && (
                <motion.div 
                    initial={{ opacity: 0, y: 10 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-start gap-3"
                >
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                        <h3 className="font-semibold text-red-800 text-sm">Error al cargar</h3>
                        <p className="text-red-500 text-sm mt-0.5">{error}</p>
                    </div>
                </motion.div>
            )}

            {/* SKELETON LOADER */}
            {loading && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 lg:gap-5">
                    {[...Array(10)].map((_, i) => (
                        <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden animate-pulse">
                            <div className="aspect-[3/4] bg-gray-200 w-full"></div>
                            <div className="p-3.5 space-y-2.5">
                                <div className="h-3.5 bg-gray-200 rounded-lg w-4/5"></div>
                                <div className="h-2.5 bg-gray-200 rounded w-3/5"></div>
                                <div className="h-2.5 bg-gray-200 rounded w-2/5"></div>
                                <div className="h-8 bg-gray-200 rounded-xl w-full mt-3"></div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* CONTENIDO: GRID DE TARJETAS */}
            {!loading && !error && (
                <>
                    {eventos.length === 0 ? (
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center py-24 bg-white rounded-3xl border border-dashed border-gray-200"
                        >
                            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-5">
                                <CalendarDays className="w-10 h-10 text-gray-300" />
                            </div>
                            <p className="text-gray-500 text-lg font-medium">No hay eventos registrados</p>
                            <p className="text-gray-400 text-sm mt-1.5">Crea el primer evento para comenzar</p>
                            {userIsAdmin && (
                                <motion.button 
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.97 }}
                                    onClick={handleOpenCreate}
                                    className="mt-5 px-6 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium shadow-lg shadow-gray-900/20 hover:bg-gray-800 transition-all"
                                >
                                    <Plus className="w-4 h-4 inline mr-1.5" />
                                    Crear evento
                                </motion.button>
                            )}
                        </motion.div>
                    ) : filteredAndSortedEventos.length === 0 ? (
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center py-24 bg-white rounded-3xl border border-dashed border-gray-200"
                        >
                            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-5">
                                <Search className="w-10 h-10 text-gray-300" />
                            </div>
                            <p className="text-gray-500 text-lg font-medium">No se encontraron eventos</p>
                            <p className="text-gray-400 text-sm mt-1.5">Prueba con otros términos de búsqueda</p>
                            <button
                                onClick={() => setSearchQuery('')}
                                className="mt-4 px-5 py-2 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-all"
                            >
                                Limpiar búsqueda
                            </button>
                        </motion.div>
                    ) : (
                        <motion.div 
                            variants={containerVariants} 
                            initial="hidden" 
                            animate="visible" 
                            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 lg:gap-5"
                        >
                            {filteredAndSortedEventos.map((evento) => {
                                const status = getEventStatus(evento.fercha_inicio, evento.fecha_fin);
                                const isActive = status.status === 'active';
                                const isSoon = status.status === 'soon';
                                const phone = extractPhoneNumber(evento.contacto);

                                return (
                                    <motion.div 
                                        key={evento.id_evento} 
                                        variants={itemVariants}
                                        whileHover={{ y: -6, transition: { duration: 0.25, ease: 'easeOut' } }} 
                                        className={`bg-white rounded-2xl shadow-sm border overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col group relative ${
                                            isActive ? 'border-red-300 shadow-red-100' : 
                                            isSoon ? 'border-amber-300 shadow-amber-100' : 
                                            'border-gray-100 hover:shadow-gray-200/50'
                                        }`}
                                    >
                                        {(isActive || isSoon) && (
                                            <div className={`h-1 w-full ${status.bg}`}>
                                                {status.pulse && (
                                                    <div className={`h-full w-full ${status.bg} animate-pulse opacity-50`}></div>
                                                )}
                                            </div>
                                        )}

                                        <EventMediaThumbnail 
                                            src={evento.mutilemedia} 
                                            alt={evento.descripcion}
                                            onExpand={(src, type) => setExpandedMedia({ src, type })}
                                        />

                                        <div className="p-3.5 flex-1 flex flex-col">
                                            <div className="mb-2">
                                                <StatusBadge fechaInicio={evento.fercha_inicio} fechaFin={evento.fecha_fin} />
                                            </div>

                                            <h3 
                                                className="font-semibold text-gray-900 text-[13px] leading-snug line-clamp-2 mb-2" 
                                                title={evento.descripcion}
                                            >
                                                {evento.descripcion}
                                            </h3>

                                            <div className="space-y-1.5 mb-3">
                                                <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                                                    <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                                                    <span className="truncate">{evento.centro_servicio}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                                                    <Calendar className="w-3 h-3 text-gray-400 shrink-0" />
                                                    <span>{formatFechaCorta(evento.fercha_inicio)}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                                                    <Clock className="w-3 h-3 text-gray-400 shrink-0" />
                                                    <span>{formatHora(evento.fercha_inicio)} - {formatHora(evento.fecha_fin)}</span>
                                                </div>
                                                {phone ? (
                                                    <a
                                                        href={`https://wa.me/${phone}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-1.5 text-[11px] group/link"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <Phone className="w-3 h-3 text-green-500 shrink-0" />
                                                        <span className="truncate text-green-600 font-medium group-hover/link:underline transition-colors">
                                                            {evento.contacto}
                                                        </span>
                                                    </a>
                                                ) : (
                                                    <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                                                        <Phone className="w-3 h-3 text-gray-400 shrink-0" />
                                                        <span className="truncate">{evento.contacto}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {userIsAdmin ? (
                                                <button 
                                                    onClick={() => handleOpenEdit(evento)} 
                                                    className="w-full py-2 bg-gray-50 hover:bg-gray-900 hover:text-white text-gray-600 border border-gray-200 hover:border-gray-900 rounded-xl text-xs font-semibold transition-all duration-200 flex items-center justify-center gap-1.5 mt-auto"
                                                >
                                                    <Pencil className="w-3 h-3" /> Editar
                                                </button>
                                            ) : (
                                                <div className="w-full py-2 bg-gray-50 text-gray-400 border border-gray-100 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5 mt-auto cursor-not-allowed">
                                                    <Lock className="w-3 h-3" /> Solo lectura
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    )}
                </>
            )}

            {/* MODAL: CREAR / EDITAR EVENTO */}
            <AnimatePresence>
                {isModalOpen && userIsAdmin && (
                    <motion.div 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }} 
                        className="fixed inset-0 bg-black/50 backdrop-blur-md z-50 flex items-center justify-center p-4"
                    >
                        <motion.div 
                            initial={{ scale: 0.92, opacity: 0, y: 30 }} 
                            animate={{ scale: 1, opacity: 1, y: 0 }} 
                            exit={{ scale: 0.92, opacity: 0, y: 30 }} 
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
                        >
                            <div className="flex justify-between items-center px-6 py-5 border-b border-gray-100 shrink-0">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">
                                        {isEditing ? 'Editar Evento' : 'Nuevo Evento'}
                                    </h2>
                                    <p className="text-sm text-gray-400 mt-0.5">
                                        {isEditing 
                                            ? `ID: ${currentEvento.id_evento}` 
                                            : 'Completa los datos del evento'}
                                    </p>
                                </div>
                                <button 
                                    onClick={() => setIsModalOpen(false)} 
                                    className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                                >
                                    <X className="w-5 h-5 text-gray-400" />
                                </button>
                            </div>

                            <form onSubmit={handleSave} className="flex-1 overflow-y-auto flex flex-col">
                                <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 flex-1">
                                    <div className="lg:col-span-2 bg-gray-50/80 p-6 border-r border-gray-100 flex flex-col">
                                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">
                                            Vista Previa
                                        </h3>

                                        <div className="mb-5">
                                            <EventMediaPreview src={formData.mutilemedia} />
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-600 mb-1.5">URL de Multimedia</label>
                                                <div className="relative">
                                                    <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                    <input 
                                                        type="url" 
                                                        name="mutilemedia" 
                                                        value={formData.mutilemedia} 
                                                        onChange={handleInputChange} 
                                                        placeholder="https://..." 
                                                        className="w-full pl-10 pr-3 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none transition-all" 
                                                    />
                                                </div>
                                                <p className="text-[10px] text-gray-400 mt-1.5 leading-relaxed">
                                                    Imágenes, videos MP4, YouTube o Vimeo
                                                </p>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Tipo:</span>
                                                <MediaTypeBadge url={formData.mutilemedia} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="lg:col-span-3 p-6 space-y-6 overflow-y-auto">
                                        {saveMessage.text && (
                                            <motion.div 
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className={`p-3 rounded-xl flex items-center gap-2.5 ${saveMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : saveMessage.type === 'error' ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-blue-50 text-blue-700 border border-blue-100'}`}
                                            >
                                                {saveMessage.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                                                <span className="text-sm font-medium">{saveMessage.text}</span>
                                            </motion.div>
                                        )}

                                        <div>
                                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">Información del Evento</h3>
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Descripción</label>
                                                    <textarea 
                                                        name="descripcion" 
                                                        value={formData.descripcion} 
                                                        onChange={handleInputChange} 
                                                        required 
                                                        rows={3}
                                                        placeholder="Describe el evento..."
                                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:bg-white focus:border-gray-900 outline-none resize-none text-sm transition-all"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                                                            <MapPin className="w-3 h-3" /> Ubicación
                                                        </label>
                                                        <input 
                                                            type="text" 
                                                            name="centro_servicio" 
                                                            value={formData.centro_servicio} 
                                                            onChange={handleInputChange} 
                                                            required 
                                                            placeholder="Sucursal Centro Lima"
                                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:bg-white focus:border-gray-900 outline-none text-sm transition-all" 
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                                                            <Phone className="w-3 h-3" /> Contacto
                                                        </label>
                                                        <input 
                                                            type="text" 
                                                            name="contacto" 
                                                            value={formData.contacto} 
                                                            onChange={handleInputChange} 
                                                            required 
                                                            placeholder="Juan Pérez - 987654321"
                                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:bg-white focus:border-gray-900 outline-none text-sm transition-all" 
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                                                <Clock className="w-3.5 h-3.5" /> Fechas y Horarios
                                            </h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Inicio</label>
                                                    <input 
                                                        type="datetime-local" 
                                                        name="fercha_inicio" 
                                                        value={formData.fercha_inicio} 
                                                        onChange={handleInputChange} 
                                                        required 
                                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:bg-white focus:border-gray-900 outline-none text-sm transition-all" 
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Fin</label>
                                                    <input 
                                                        type="datetime-local" 
                                                        name="fecha_fin" 
                                                        value={formData.fecha_fin} 
                                                        onChange={handleInputChange} 
                                                        required 
                                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:bg-white focus:border-gray-900 outline-none text-sm transition-all" 
                                                    />
                                                </div>
                                            </div>
                                            {formData.fercha_inicio && formData.fecha_fin && (
                                                <p className="text-[11px] text-gray-400 mt-2">
                                                    Duración: {Math.ceil((new Date(formData.fecha_fin) - new Date(formData.fercha_inicio)) / (1000 * 60 * 60))} horas
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end items-center gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 shrink-0">
                                    <button 
                                        type="button" 
                                        onClick={() => setIsModalOpen(false)} 
                                        disabled={saving} 
                                        className="px-5 py-2.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all disabled:opacity-50 font-medium"
                                    >
                                        Cancelar
                                    </button>
                                    <button 
                                        type="submit" 
                                        disabled={saving} 
                                        className="flex items-center gap-2 px-6 py-2.5 text-sm bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-gray-900/20 font-medium"
                                    >
                                        {saving ? (
                                            <><RefreshCw className="w-4 h-4 animate-spin" /> Guardando...</>
                                        ) : (
                                            <><Save className="w-4 h-4" /> {isEditing ? 'Guardar Cambios' : 'Crear Evento'}</>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* MODAL: MULTIMEDIA EXPANDIDA */}
            <AnimatePresence>
                {expandedMedia && (
                    <motion.div 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 bg-black/85 backdrop-blur-md z-[60] flex items-center justify-center p-4"
                        onClick={() => setExpandedMedia(null)}
                    >
                        <motion.div 
                            initial={{ scale: 0.85, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.85, opacity: 0 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="relative w-full max-w-5xl max-h-[90vh] flex flex-col items-center"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <motion.button 
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.15 }}
                                onClick={() => setExpandedMedia(null)}
                                className="absolute -top-14 right-0 p-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full transition-all z-10"
                            >
                                <X className="w-5 h-5 text-white" />
                            </motion.button>

                            {expandedMedia.type === 'image' && (
                                <div className="relative max-w-full max-h-[80vh]">
                                    <img 
                                        src={expandedMedia.src} 
                                        alt="Vista ampliada"
                                        className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl"
                                    />
                                </div>
                            )}

                            {expandedMedia.type === 'video' && (
                                <div className="w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
                                    <video 
                                        src={expandedMedia.src}
                                        controls
                                        autoPlay
                                        className="w-full h-full"
                                    >
                                        Tu navegador no soporta reproducción de video.
                                    </video>
                                </div>
                            )}

                            {expandedMedia.type === 'youtube' && (
                                <div className="w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
                                    <iframe
                                        src={`https://www.youtube.com/embed/${extractYouTubeId(expandedMedia.src)}?autoplay=1`}
                                        title="YouTube video player"
                                        className="w-full h-full"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </div>
                            )}

                            {expandedMedia.type === 'vimeo' && (
                                <div className="w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
                                    <iframe
                                        src={`https://player.vimeo.com/video/${extractVimeoId(expandedMedia.src)}?autoplay=1`}
                                        title="Vimeo video player"
                                        className="w-full h-full"
                                        allow="autoplay; fullscreen; picture-in-picture"
                                        allowFullScreen
                                    />
                                </div>
                            )}

                            <motion.div 
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="mt-5 flex items-center gap-2 bg-white/10 backdrop-blur-md px-5 py-2.5 rounded-full"
                            >
                                <ExternalLink className="w-3.5 h-3.5 text-white/60" />
                                <span className="text-xs text-white/70 max-w-md truncate font-mono">{expandedMedia.src}</span>
                            </motion.div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Eventos;